import React, { useState, useEffect } from "react";
import { 
  Calculator, Target, GraduationCap, Users, Globe, Clock, AlertCircle, 
  BookOpen, TrendingUp, ChevronRight 
} from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { Link } from "react-router-dom";

const DETPage = () => {
  const [detScore, setDETScore] = useState(110);
  const [scoreBand, setScoreBand] = useState("B2+/C1");

  const detScores = [10,15,20,25,30,35,40,45,50,55,60,65,70,75,80,85,90,95,100,105,110,115,120,125,130,135,140,145,150,155,160];

  const updateScore = () => {
    const bands = {
      10: "A1", 40: "A2", 70: "B1", 90: "B2", 
      110: "B2+/C1", 125: "C1", 140: "C1+/C2"
    };
    const closestScore = detScores.reduce((prev, curr) => 
      Math.abs(curr - detScore) < Math.abs(prev - detScore) ? curr : prev
    );
    const band = Object.keys(bands).reverse().find(key => closestScore >= parseInt(key)) || "A1";
    setScoreBand(bands[band] || band);
  };

  useEffect(() => {
    updateScore();
  }, [detScore]);

  const selfSelectCards = [
    { icon: GraduationCap, title: "US/Canada University Applicants", desc: "Accepted by 5,000+ institutions primarily in US & Canada. Acceptance varies by program" },
    { icon: Users, title: "Timeline-Constrained Applicants", desc: "48-hour results ideal when deadlines approach and DET is explicitly accepted" },
    { icon: TrendingUp, title: "DET-Accepting Universities", desc: "Best when target schools explicitly list DET. IELTS/PTE remain safer for visas" }
  ];

  const benchmarks = [
    { score: "90–105", meaning: "B2 level", unis: "Many associate/community college programs" },
    { score: "110–120", meaning: "B2+/C1", unis: "Program-dependent undergraduate admissions" },
    { score: "125–135", meaning: "C1", unis: "Competitive programs where DET accepted" },
    { score: "140+", meaning: "C1+/C2", unis: "Highly selective programs (varies significantly)" }
  ];

  const myths = [
    { text: "Duolingo is easier than IELTS/PTE", counter: "Same CEFR levels. Different format may suit some skills better" },
    { text: "Unlimited attempts without consequences", counter: "10-day cooldown between tests. Universities see all scores" },
    { text: "All universities accept Duolingo", counter: "5,000+ accept, thousands do not. Always verify target schools" },
    { text: "DET accepted for student visas", counter: "Rarely accepted for UKVI/Australia visas. Verify government requirements" }
  ];

  const testFormat = [
    { section: "Literacy (Reading + Writing)", duration: "45 min (adaptive)" },
    { section: "Oral Proficiency (Speaking + Listening)", duration: "15 min (adaptive)" }
  ];

  const preparationFlow = [
    { step: "DET Format Mastery", desc: "Interactive speaking, extended writing, fill-in-blanks, keyboard fluency practice" },
    { step: "Adaptive Strategy", desc: "Optimize early questions for higher second-half difficulty" },
    { step: "Speaking Discipline", desc: "Clear pronunciation, 30-90s timing, camera/mic positioning" },
    { step: "Full DET Simulations", desc: "1-hour adaptive mocks with official 10-160 score reports" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* 1. Hero + Calculator */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-light text-slate-900 leading-tight">
              Duolingo English Test Overview
            </h1>
            <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
              Online, on-demand English proficiency test accepted by 5,000+ universities primarily in US & Canada. AI-proctored, results in 48 hours.
            </p>
            <p className="text-lg text-slate-500">
              DET best when explicitly accepted by target universities. IELTS/PTE remain safer choices for visas and global portability. Scores aligned to CEFR proficiency levels.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-100 max-h-[32rem] overflow-y-auto"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-600" />
              <h3 className="text-xl font-semibold text-slate-900">Estimate Your DET Score</h3>
            </div>
            <p className="text-xs text-slate-500 mb-6 italic">
              Holistic adaptive scoring across integrated skills. Scores interpreted independently by universities.
            </p>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Estimated Overall Score (10-160)</label>
                <input
                  type="range"
                  min="10" max="160"
                  step="5"
                  value={detScore}
                  onChange={e => setDETScore(+e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                />
                <span className="text-sm font-mono text-slate-600">{detScore}</span>
              </div>
              <div className="pt-6 border-t border-slate-200">
                <div className="text-3xl font-bold text-slate-900 mb-1">DET: {detScore}</div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <span className="font-semibold">{scoreBand}</span>
                  <span className="text-sm">(CEFR Level)</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Who Should Take Duolingo */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Who Should Take DET?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Strong for US/Canada admissions when explicitly accepted. Limited for UK/Australia visas and graduate programs requiring IELTS.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {selfSelectCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 hover:border-emerald-200"
            >
              <card.icon className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CTA Cluster */}
      <section className="py-24 px-6 md:px-12 bg-emerald-50 border-t border-emerald-200">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Next Steps</h3>
              <p className="text-sm text-slate-600">Review DET preparation for your target universities</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/test-prep#duolingo">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl flex items-center gap-2"
            >
              DET Preparation Programs
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            </Link>
            <Link to="/test-prep">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border-2 border-slate-600 hover:border-slate-400 bg-white text-slate-900 px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-2"
            >
              All Test Prep
            </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Score Benchmarks */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            What Your Score Means
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {benchmarks.map((bench, i) => (
            <motion.div
              key={bench.score}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 hover:shadow-xl border border-slate-100"
            >
              <div className="text-3xl font-bold text-emerald-600 mb-4">{bench.score}</div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">{bench.meaning}</h4>
              <p className="text-slate-600">{bench.unis}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. Institutional Acceptance */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Institutional Acceptance
          </h2>
          <p className="text-xl text-slate-600">
            5,000+ universities (primarily US/Canada). Not typically accepted for student visas.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-3xl p-10">
            <Globe className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">University Admissions</h3>
            <p className="text-lg text-slate-600">Strong acceptance in US/Canada. Selective programs may prefer IELTS/PTE. Always verify program requirements.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-10">
            <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Limitations</h3>
            <p className="text-lg text-slate-600">UK/Australia student visas typically require IELTS/PTE. Graduate admissions acceptance varies widely.</p>
          </motion.div>
        </div>
      </section>

      {/* 6. Common Misconceptions */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myths.map((myth, i) => (
              <motion.div
                key={myth.text}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col gap-4 p-6 bg-white rounded-2xl border border-slate-200 hover:border-amber-200 group"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-slate-700 group-hover:text-slate-900 font-medium">{myth.text}</p>
                </div>
                <p className="text-sm text-slate-600 font-semibold bg-amber-50 p-3 rounded-xl">
                  {myth.counter}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Test Structure */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Test Structure
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-16">
            Total: 1 hour (adaptive). Short duration but cognitively intensive due to continuous adaptive tasks.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {testFormat.map((format, i) => (
            <motion.div
              key={format.section}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 text-center border border-slate-100 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{format.section}</h3>
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <div className="text-2xl font-bold text-slate-900">{format.duration}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. How We Help You Prepare */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            How We Help You Prepare
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Targeted preparation for DET's unique adaptive format and AI evaluation criteria.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {preparationFlow.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: i % 2 ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-10 border border-slate-100 hover:shadow-xl hover:border-emerald-200"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.step}</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9. ContactForm */}
      <section id="contact-form" className="max-w-6xl mx-auto px-8 pb-12 bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Questions About DET?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Schedule consultation for university-specific DET requirements and preparation strategy.
          </p>
        </div>
        <ContactForm />
      </section>
    </div>
  );
};

export default DETPage;
